import React, { useState } from 'react';
import NSSearchBar from 'bricks/NSSearchBar/NSSearchBar';
import { formatDecimalAsPercentage, numeralFormatter, numeralFormatterCurrency } from 'ns_libs/formatter';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import {
    COST_BASIS_TYPES, LOAN_CALCULATION_OPTIONS, UNIT_OF_TIME_TYPES, UNIT_OF_TIME_TYPES_UI,
} from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import {
    BOE_UNIT_TYPE_UI, LOAN_TERM_TIME, BOE_TITLES, BOE_SUBTITLES,
} from './constants';
import './BackOfEnvelopeKeyValues.scss';
import BackOfEnvelopeKeyValuesSection from './BackOfEnvelopeKeyValuesSection/BackOfEnvelopeKeyValuesSection';

export interface IBackOfEnvelopeKeyValuesProps {
    boeScenario: BOEScenario | undefined;
    className?: string;
}

const BackOfEnvelopeKeyValues = ({ boeScenario, className }: IBackOfEnvelopeKeyValuesProps) => {
    const { showCents } = useProFormaSocketContext();

    const [searchValue, setSearchValue] = useState<string>('');

    const showCentsFormat = showCents ? '$0,0.00' : '$0,0';

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    };

    let debtMetricType = LOAN_CALCULATION_OPTIONS[1].label;
    let debtMetricAmount = formatDecimalAsPercentage(boeScenario?.loanToCost || 0, 2);
    if (boeScenario?.loanToValue != null) {
        debtMetricType = LOAN_CALCULATION_OPTIONS[0].label;
        debtMetricAmount = formatDecimalAsPercentage(boeScenario?.loanToValue || 0, 2);
    } else if (boeScenario?.debtYield != null) {
        debtMetricType = LOAN_CALCULATION_OPTIONS[2].label;
        debtMetricAmount = formatDecimalAsPercentage(boeScenario?.debtYield || 0, 2);
    }

    const PER_UNIT_SUBDIVS = [
        COST_BASIS_TYPES.PER_UNIT,
        COST_BASIS_TYPES.PER_KW,
        COST_BASIS_TYPES.PER_MW,
    ];
    const incomeUnitOfTime = boeScenario?.rentPriceTime === UNIT_OF_TIME_TYPES.YEARLY ? UNIT_OF_TIME_TYPES_UI.ANNUAL : UNIT_OF_TIME_TYPES_UI.MONTHLY;
    const incomeSectionRentPrice = PER_UNIT_SUBDIVS.includes(boeScenario?.rentPriceSubdiv as COST_BASIS_TYPES)
        ? numeralFormatterCurrency(boeScenario?.monthlyRentPricePerUnit || 0, showCentsFormat)
        : numeralFormatterCurrency(boeScenario?.monthlyRentPricePerSf || 0, showCentsFormat);

    const capitalizedIncomeUnitOfTime
        = incomeUnitOfTime.charAt(0).toUpperCase() + incomeUnitOfTime.slice(1);

    const expenseUnitOfTime = boeScenario?.expenseCostTime === UNIT_OF_TIME_TYPES.YEARLY ? UNIT_OF_TIME_TYPES_UI.ANNUAL : UNIT_OF_TIME_TYPES_UI.MONTHLY;
    const expenseSectionRentPrice = PER_UNIT_SUBDIVS.includes(boeScenario?.expenseCostSubdiv as COST_BASIS_TYPES)
        ? numeralFormatterCurrency(boeScenario?.monthlyExpenseCostPerUnit || 0, showCentsFormat)
        : numeralFormatterCurrency(boeScenario?.monthlyExpenseCostPerSf || 0, showCentsFormat);
    const capitalizedExpenseUnitOfTime
        = expenseUnitOfTime.charAt(0).toUpperCase() + expenseUnitOfTime.slice(1);

    const hardCostsAmountPerUnitType = boeScenario?.hardCostsSubdiv === COST_BASIS_TYPES.TOTAL
        ? Number(boeScenario?.hardCostsTotal || 0) / Number(boeScenario?.builtSf || 1)
        : boeScenario?.hardCosts || 0;

    const softCostsAmountPerUnitType = boeScenario?.softCostsSubdiv === COST_BASIS_TYPES.TOTAL
        ? Number(boeScenario?.softCostsTotal || 0) / Number(boeScenario?.builtSf || 1)
        : boeScenario?.softCosts || 0;

    const loanTerm = Number(boeScenario?.loanTermMonths || 0) === 1
        ? LOAN_TERM_TIME[boeScenario?.loanTermTime || UNIT_OF_TIME_TYPES.YEARLY]
        : `${LOAN_TERM_TIME[boeScenario?.loanTermTime || UNIT_OF_TIME_TYPES.YEARLY]}s`;

    const sections = [
        {
            title: BOE_TITLES.PROPERTY_SIZE,
            subtitles: [
                { label: BOE_SUBTITLES.BUILT_GROSS_SF, value: numeralFormatter(boeScenario?.builtSf || 0) },
                { label: BOE_SUBTITLES.EFFICIENCY_RATIO, value: formatDecimalAsPercentage(boeScenario?.buildingEfficiency || 0, 2) },
                { label: BOE_SUBTITLES.NET_RENTABLE_SF, value: numeralFormatter(boeScenario?.netRentableSf || 0) },
            ],
        },
        {
            title: BOE_TITLES.INCOME,
            subtitles: [
                {
                    label: BOE_SUBTITLES.GROSS_POTENTIAL_RENT,
                    value: `${numeralFormatterCurrency(boeScenario?.grossPotentialRent || 0, showCentsFormat)} annually`,
                },
                { label: `${capitalizedIncomeUnitOfTime} rent price`, value: `${incomeSectionRentPrice} per ${boeScenario?.rentPriceSubdiv}` },
                { label: BOE_SUBTITLES.UNITS, value: boeScenario?.numberOfUnits || 0 },
                { label: BOE_SUBTITLES.SF_PER_UNIT, value: numeralFormatter(boeScenario?.sfPerUnit || 0) },
            ],
        },
        {
            title: BOE_TITLES.EXPENSES,
            subtitles: [
                {
                    label: BOE_SUBTITLES.NET_OPERATING_INCOME,
                    value: `${numeralFormatterCurrency(boeScenario?.netOperatingIncome || 0, showCentsFormat)} annually`,
                },
                { label: `${capitalizedExpenseUnitOfTime} expense price`, value: `${expenseSectionRentPrice} per ${boeScenario?.expenseCostSubdiv}` },
                { label: BOE_SUBTITLES.EXPENSES_TOTAL, value: numeralFormatterCurrency(boeScenario?.expensesTotal || 0, showCentsFormat) },
            ],
        },
        {
            title: BOE_TITLES.EXIT_VALUE,
            subtitles: [
                { label: BOE_SUBTITLES.EXIT_VALUATION, value: numeralFormatterCurrency(boeScenario?.exitValuation || 0, showCentsFormat) },
                { label: BOE_SUBTITLES.ASSUMED_EXIT_CAP_RATE, value: formatDecimalAsPercentage(boeScenario?.assumedExitCapRate || 0, 2) },
            ],
        },
        {
            title: BOE_TITLES.DEVELOPMENT_COSTS,
            subtitles: [
                {
                    label: BOE_SUBTITLES.TOTAL_DEVELOPMENT_COSTS,
                    value: numeralFormatterCurrency(boeScenario?.totalDevelopmentCosts || 0, showCentsFormat),
                },
                { label: BOE_SUBTITLES.ACQUISITION_COSTS, value: numeralFormatterCurrency(boeScenario?.acquisition || 0, showCentsFormat) },
                {
                    label: BOE_SUBTITLES.HARD_COSTS,
                    value: `${numeralFormatterCurrency(hardCostsAmountPerUnitType, showCentsFormat)}/
                        ${BOE_UNIT_TYPE_UI[boeScenario?.hardCostsSubdiv || COST_BASIS_TYPES.PER_SF]} •
                        ${numeralFormatterCurrency(boeScenario?.hardCostsTotal || 0, showCentsFormat)} total`,
                },
                {
                    label: BOE_SUBTITLES.SOFT_COSTS,
                    value: `${numeralFormatterCurrency(softCostsAmountPerUnitType, showCentsFormat)}/
                    ${BOE_UNIT_TYPE_UI[boeScenario?.softCostsSubdiv || COST_BASIS_TYPES.PER_SF]} •
                    ${numeralFormatterCurrency(boeScenario?.softCostsTotal || 0, showCentsFormat)} total`,
                },
                { label: BOE_SUBTITLES.DEVELOPER_FEE, value: formatDecimalAsPercentage(boeScenario?.developerFeeRate || 0, 2) },
                { label: BOE_SUBTITLES.APR, value: formatDecimalAsPercentage(boeScenario?.annualPercentageRate || 0, 2) },
                { label: BOE_SUBTITLES.AVERAGE_LOAN_BALANCE, value: numeralFormatterCurrency(boeScenario?.averageLoanBalance || 0, showCentsFormat) },
                { label: BOE_SUBTITLES.LOAN_TERM, value: `${boeScenario?.loanTermMonths} ${loanTerm}` },
                { label: BOE_SUBTITLES.INTEREST_RESERVE_TOTAL, value: numeralFormatterCurrency(boeScenario?.interestReserveTotal || 0, showCentsFormat) },
            ],
        },
        {
            title: BOE_TITLES.CONSTRUCTION_FINANCING,
            subtitles: [
                { label: BOE_SUBTITLES.LOAN_AMOUNT, value: numeralFormatterCurrency(boeScenario?.loanAmount || 0, showCentsFormat) },
                { label: BOE_SUBTITLES.REQUIRED_EQUITY, value: numeralFormatterCurrency(boeScenario?.requiredEquity || 0, showCentsFormat) },
                { label: debtMetricType, value: debtMetricAmount },
            ],
        },
        {
            title: BOE_TITLES.RETURNS,
            subtitles: [
                { label: BOE_SUBTITLES.RETURN_ON_COST, value: formatDecimalAsPercentage(boeScenario?.returnOnCost || 0, 2) },
                { label: BOE_SUBTITLES.RETURN_ON_EQUITY, value: formatDecimalAsPercentage(boeScenario?.returnOnEquity || 0, 2) },
                { label: BOE_SUBTITLES.PROFIT_RETURN, value: numeralFormatterCurrency(boeScenario?.profitReturn || 0, showCentsFormat) },
            ],
        },
    ];

    return (
        <div className={`font-12 m-0 BackOfEnvelopeKeyValues ${className}`}>
            <div>
                <NSSearchBar
                    searchValue={searchValue}
                    handleChangeSearchValue={handleSearchChange}
                    placeholder="Search"
                />
            </div>

            {sections.map(section => (
                <BackOfEnvelopeKeyValuesSection
                    key={section.title}
                    title={section.title}
                    subtitles={section.subtitles}
                    searchTerm={searchValue}
                />
            ))}
        </div>
    );
};

export default BackOfEnvelopeKeyValues;
